import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { AgGridTable } from "../../Component/AgGridTable";
import { GetTimeSheet as TimeSheetType } from "../../Interface/TimeSheetInterface";
import {
  fetchCustomers,
  fetchProjects,
  GetProjectByCustomerId,
  fetchGetYears,
  fetchGetMonths,
  fetchGetTimeSheet,
} from "../../services/timesheetService";
import { EnhancedPageTitle } from "../../Component/PageTitle";
import { Timeline } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { FilterField, SearchDrawer } from "../../Component/SearchDrawer";
import { GridApi } from "ag-grid-community";

const TimeSheetList: React.FC = () => {
  const currentDate = new Date();
  const defaultMonthName = currentDate.toLocaleString("default", { month: "short" });
  const defaultYear = currentDate.getFullYear().toString();

  const [timesheets, setTimesheets] = useState<TimeSheetType[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [customers, setCustomers] = useState<{ customerId: number; customerName: string }[]>([]);
  const [projects, setProjects] = useState<{ projectId: number; projectName: string }[]>([]);
  const [years, setYears] = useState<{ tableID: any; tableValueLong: any }[]>([]);
  const [months, setMonths] = useState<{ tableID: any; tableName: any }[]>([]);

  const [customer, setCustomer] = useState<number | "">("");
  const [project, setProject] = useState<number | "">("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [totalHours, setTotalHours] = useState<number>(0);


  const [drawerOpenState, setDrawerOpenState] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({
    timesheetMonth: defaultMonthName,
    timesheetYear: defaultYear,
    customerName: "",
    projectName: "",
  });

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const gridApi = useRef<GridApi | null>(null);

  const monthOptions = months.map((m) => m.tableName);
  const yearOptions = years.map((y) => y.tableValueLong?.toString());
  const customerOptions = customers.map((c) => c.customerName);
  const projectOptions = projects.map((p) => p.projectName);

  const exportFilters: FilterField[] = [
    { key: "timesheetMonth", label: "Month", type: "dropdown", options: monthOptions },
    { key: "timesheetYear", label: "Year", type: "dropdown", options: yearOptions },
    { key: "customerName", label: "Customer", type: "dropdown", options: customerOptions },
    { key: "projectName", label: "Project", type: "dropdown", options: projectOptions },
  ];

  const loadTimeSheets = async (filterValues?: Record<string, string>) => {
    try {
      const response = await fetchGetTimeSheet(filterValues || {});
      const data = Array.isArray(response.data) ? response.data : [];
      setTimesheets(data);
      // Calculate total hours
    const total = data.reduce(
      (sum: number, item: { totalHoursBooked: string; }) => sum + (parseFloat(item.totalHoursBooked) || 0),
      0
    );
    setTotalHours(total);
    } catch (error) {
      console.error("Error loading timesheets:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const customerData = await fetchCustomers();
        setCustomers(Array.isArray(customerData.data) ? customerData.data : []);

        const yearData = await fetchGetYears();
        const fetchedYears = Array.isArray(yearData.data) ? yearData.data : [];
        setYears(fetchedYears);

        const monthData = await fetchGetMonths();
        const fetchedMonths = Array.isArray(monthData.data) ? monthData.data : [];
        setMonths(fetchedMonths);

        const matchedMonth = fetchedMonths.find(
          (m: { tableName: string; }) => m.tableName?.toLowerCase() === defaultMonthName.toLowerCase()
        );
        const matchedYear = fetchedYears.find(
          (y: { tableValueLong: { toString: () => string; }; }) => y.tableValueLong?.toString() === defaultYear
        );

        setMonth(matchedMonth?.tableID || "");
        setYear(matchedYear?.tableID || "");

        if (customerData.data.length > 0) {
          const firstCustomerId = customerData.data[0].customerId;
          const projectData = await GetProjectByCustomerId(firstCustomerId);
          setProjects(Array.isArray(projectData.data) ? projectData.data : []);
        }

        const initialFilters = {
          timesheetMonth: matchedMonth?.tableName || "",
          timesheetYear: matchedYear?.tableValueLong?.toString() || "",
          customerName: "",
          projectName: "",
        };
        setFilters(initialFilters);

        await loadTimeSheets(initialFilters);
      } catch (error) {
        console.error("Failed to load initial data:", error);
      } finally {
        setInitialLoading(false); // hide loader after first load
      }
    };

    loadData();
  }, []);

  const handleRowClick = (data: TimeSheetType) => {
    navigate(`/Timesheet/edit/${data.timesheetID}`);
  };

  const handleSearch = async (searchFilters: Record<string, string>) => {
    setDrawerOpenState(false);
    setFilters(searchFilters);

    const selectedMonth = months.find(m => m.tableName === searchFilters.timesheetMonth);
    const selectedYear = years.find(y => y.tableValueLong?.toString() === searchFilters.timesheetYear);

    setMonth(selectedMonth?.tableID || "");
    setYear(selectedYear?.tableID || "");

    const selectedCustomer = customers.find(c => c.customerName === searchFilters.customerName);
    if (selectedCustomer) {
      try {
        const projectRes = await GetProjectByCustomerId(selectedCustomer.customerId);
        const projectList = Array.isArray(projectRes.data) ? projectRes.data : [];
        setProjects(projectList);

        const selectedProject = projectList.find(p => p.projectName === searchFilters.projectName);
        if (selectedProject) setProject(selectedProject.projectId);
      } catch (error) {
        console.error("Project fetch error:", error);
        setProjects([]);
        setProject("");
      }
    } else {
      setProjects([]);
      setProject("");
    }

    await loadTimeSheets(searchFilters);
  };

  const handleReset = () => {
    setDrawerOpenState(false);
    const defaultMonth = currentDate.toLocaleString("default", { month: "short" });
    const defaultYr = currentDate.getFullYear().toString();

    const resetFilters = {
      timesheetMonth: defaultMonth,
      timesheetYear: defaultYr,
      customerName: "",
      projectName: "",
    };

    setFilters(resetFilters);
    setCustomer("");
    setProject("");
    setProjects([]);

    loadTimeSheets(resetFilters);
  };

  return (
    <Box sx={{ padding: 3, backgroundColor: "rgb(238, 242, 246)" }}>
      {initialLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
         <EnhancedPageTitle
  title={`Timesheet Dashboard – ${totalHours.toLocaleString()} hrs`}
  icon={<Timeline />}
  button1Text="Add Timesheet"
  button1Action={() => navigate("/timesheet/add")}
  button2Text="Search"
  button2Action={() => setDrawerOpenState(true)}
/>

          <SearchDrawer
            open={drawerOpenState}
            onClose={() => setDrawerOpenState(false)}
            onSearch={handleSearch}
            onReset={handleReset}
            filterFields={exportFilters}
            onCustomerChange={async (selectedCustomerName: string) => {
              const selectedCustomer = customers.find(c => c.customerName === selectedCustomerName);
              if (selectedCustomer) {
                try {
                  const projectRes = await GetProjectByCustomerId(selectedCustomer.customerId);
                  const projectList = Array.isArray(projectRes.data.data) ? projectRes.data.data : [];
                  setProjects(projectList);
                } catch (error) {
                  console.error("Error loading projects:", error);
                  setProjects([]);
                }
              } else {
                setProjects([]);
              }
            }}
          />

          <AgGridTable
            domLayout="autoHeight"
            customStyles="legacy"
            columns={[
   {
    field: "customerName",
    headerName: "Customer",
    sortable: true,
    filter:true,
    flex: 2,
    cellStyle: { textAlign: "left" },
    headerClass: "ag-left-aligned-header",

  },
  {
    field: "projectName",
    headerName: "Project",
    filter:true,
    sortable: true,
    flex: 2,
    cellStyle: { textAlign: "left" },
    headerClass: "ag-left-aligned-header",

  },
  {
    field: "timesheetMonth",
    headerName: "Month",
    sortable: true,
    filter: true,
    flex: 0.7,
    cellStyle: { textAlign: "center" },
     headerClass: "ag-center-header",
  },
  {
    field: "timesheetYear",
    headerName: "Year",
    sortable: true,
    filter:true,
    flex: 0.3,
    cellStyle: { textAlign: "center" },
     headerClass: "ag-center-header",
  },
  {
    field: "totalHoursBooked",
    headerName: "Hours Booked",
    sortable: true,
    filter:true,
    flex: 0.3,
    cellStyle: { textAlign: "right", paddingRight: "30px" },
     headerClass: "ag-center-header",
  },
  {
    field: "employeeCount",
    headerName: "Emp Count",
    sortable: true,
    flex: 0.3,
    cellStyle: { textAlign: "right", paddingRight: "30px" },
     headerClass: "ag-center-header",
  },
]}

            
            data={timesheets}
            pagination={false}
            rowSelection="single"
            gridOptions={{
              onRowClicked: (event) => handleRowClick(event.data),
            }}
            handleSelectedRows={() => {}}
            noDataComponentText="No timesheet data available"
            paginationPerPage={0}
          />
        </>
      )}
    </Box>
  );
};

export default TimeSheetList;